import React, { useState } from "react";
import {
  MoreVertical,
  Users,
  UserPlus,
  Trash2,
  Eye,
  Loader2,
  Building,
} from "lucide-react";

const PartnerTeamsTab = ({
  teams = [],
  isLoading = false,
  searchTerm = "",
  onOpenCreateModal,
  onViewTeam,
  onAddMember,
  onDeleteTeam,
}) => {
  const [activeMenuId, setActiveMenuId] = useState(null);

  const toggleMenu = (id, e) => {
    e.stopPropagation();
    setActiveMenuId((prev) => (prev === id ? null : id));
  };

  React.useEffect(() => {
    const handleOutside = () => setActiveMenuId(null);
    window.addEventListener("click", handleOutside);
    return () => window.removeEventListener("click", handleOutside);
  }, []);

  if (isLoading) {
    return (
      <div className="net-loading-wrap">
        <Loader2 size={36} className="net-spin" style={{ color: "#9333ea" }} />
        <p style={{ fontWeight: 600 }}>Loading partner teams...</p>
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="net-empty">
        <div className="net-empty__icon-wrap" style={{ background: "#faf5ff", color: "#9333ea" }}>
          <Users size={32} />
        </div>
        <h3 className="net-empty__title">
          {searchTerm ? "No matching teams found" : "No Partner Teams Created Yet"}
        </h3>
        <p className="net-empty__desc">
          {searchTerm
            ? `No teams matched "${searchTerm}". Try a different name.`
            : "Build your sales agency teams, manage sub-agents, allocate seats, and monitor team performance seamlessly."}
        </p>
        <button
          type="button"
          className="net-btn net-btn--primary"
          style={{ background: "#9333ea" }}
          onClick={onOpenCreateModal}
        >
          <Users size={16} /> Create First Team
        </button>
      </div>
    );
  }

  return (
    <div className="net-grid">
      {teams.map((t) => {
        const id = t.id;
        const name = t.name || "Partner Team";
        const desc = t.description || "";
        const totalSeats = Number(t.total_seats) || 5;
        const usedSeats = Number(t.used_seats) || 0;
        const availableSeats = Number(t.available_seats) || Math.max(0, totalSeats - usedSeats);
        const percentUsed = Math.min(100, Math.round((usedSeats / totalSeats) * 100));
        const status = t.status || "ACTIVE";

        return (
          <div key={id} className="net-team-card">
            {/* Top Area */}
            <div className="net-team-card__top">
              <div onClick={() => onViewTeam(t)} style={{ cursor: "pointer", flex: 1, minWidth: 0 }}>
                <h3 className="net-team-card__title" title={name}>
                  {name}
                </h3>
                <span
                  style={{
                    background: status === "ACTIVE" ? "#f0fdf4" : "#f1f5f9",
                    color: status === "ACTIVE" ? "#16a34a" : "#64748b",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    padding: "0.15rem 0.55rem",
                    borderRadius: "9999px",
                    border: `1px solid ${status === "ACTIVE" ? "#bbf7d0" : "#e2e8f0"}`,
                  }}
                >
                  {status}
                </span>
              </div>

              {/* Context Menu */}
              <div className="net-menu-wrapper">
                <button
                  type="button"
                  className="net-menu-trigger"
                  onClick={(e) => toggleMenu(id, e)}
                  title="Actions"
                >
                  <MoreVertical size={16} />
                </button>

                {activeMenuId === id && (
                  <div className="net-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="net-dropdown-item"
                      onClick={() => {
                        setActiveMenuId(null);
                        onViewTeam(t);
                      }}
                    >
                      <Eye size={14} /> View Team Roster
                    </button>
                    <button
                      type="button"
                      className="net-dropdown-item"
                      onClick={() => {
                        setActiveMenuId(null);
                        onAddMember(t);
                      }}
                    >
                      <UserPlus size={14} /> Add Member
                    </button>
                    <div className="net-dropdown-divider" />
                    <button
                      type="button"
                      className="net-dropdown-item net-dropdown-item--danger"
                      onClick={() => {
                        setActiveMenuId(null);
                        onDeleteTeam(t);
                      }}
                    >
                      <Trash2 size={14} /> Delete Team
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <p className="net-team-card__desc">
              {desc || "Partner team members and regional sales coordinators."}
            </p>

            {/* Seats Bar */}
            <div className="net-team-seats-bar">
              <div
                className="net-team-seats-fill"
                style={{
                  width: `${percentUsed}%`,
                  background: percentUsed >= 90 ? "#ef4444" : "#9333ea",
                }}
              />
            </div>

            <div className="net-team-seats-legend">
              <span>{usedSeats} / {totalSeats} seats used</span>
              <span>{availableSeats} available</span>
            </div>

            {/* Quick Actions */}
            <div className="net-card__actions-row">
              <button
                type="button"
                className="net-quick-btn"
                onClick={() => onViewTeam(t)}
                title="View Roster"
              >
                <Eye size={13} /> View Roster
              </button>
              <button
                type="button"
                className="net-quick-btn"
                style={{ color: "#9333ea", background: "#faf5ff", borderColor: "#f3e8ff" }}
                onClick={() => onAddMember(t)}
                title="Add Member"
              >
                <UserPlus size={13} /> Add Member
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PartnerTeamsTab;
